import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Megaphone, Loader2, Users, Star, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const FILTERS = [
  { value: 'recent_30d', label: 'Clienti recenti (ultimi 30 giorni)', icon: Clock, hint: 'Più probabilità di prenotare subito' },
  { value: 'vip', label: 'Solo clienti VIP', icon: Star, hint: 'Clienti con tag "vip" nel CRM' },
  { value: 'all', label: 'Tutti i clienti', icon: Users, hint: 'Invio massivo — usa con moderazione' },
];

const SlotBroadcastDialog = ({ open, onOpenChange, slotDateTime, slotDuration = 30, serviceName, appointmentId, onSent }) => {
  const { API, getAuthHeaders } = useAuth();
  const [filter, setFilter] = useState('recent_30d');
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!open) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/broadcast-slot/preview`, {
        params: { client_filter: filter },
        ...getAuthHeaders(),
      });
      setPreviewCount(res.data.count);
    } catch (err) {
      console.error(err);
      setPreviewCount(0);
    } finally {
      setPreviewLoading(false);
    }
  }, [API, getAuthHeaders, filter, open]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const sendBroadcast = async () => {
    if (!slotDateTime) {
      toast.error('Slot non valido');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/calendar/broadcast-slot`,
        {
          slot_datetime: slotDateTime,
          slot_duration_min: slotDuration,
          client_filter: filter,
          service_name: serviceName || null,
          appointment_id: appointmentId || null,
        },
        getAuthHeaders()
      );
      const { count_sent, count_failed } = res.data;
      if (count_sent > 0) {
        toast.success(`Notifica inviata a ${count_sent} cliente${count_sent > 1 ? 'i' : ''}!`, {
          description: count_failed > 0 ? `${count_failed} invii falliti` : 'Tutti i messaggi sono stati consegnati a Meta.',
        });
      } else {
        toast.warning('Nessun messaggio inviato', {
          description: 'Verifica che i clienti selezionati abbiano un numero WhatsApp valido.',
        });
      }
      onSent?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error('Errore invio', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  const slotDate = slotDateTime ? parseISO(slotDateTime) : null;
  const dateLabel = slotDate ? format(slotDate, "EEEE d MMMM", { locale: it }) : '';
  const timeLabel = slotDate ? format(slotDate, 'HH:mm') : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="slot-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-primary" />
            Avvisa Clienti dello Slot Libero
          </DialogTitle>
          <DialogDescription>
            Invia il template WhatsApp <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">slot_disponibile_01</span> ai clienti per riempire questo slot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Slot info */}
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <p className="font-semibold capitalize">{dateLabel} alle {timeLabel}</p>
            {serviceName && (
              <p className="text-muted-foreground text-xs mt-0.5">Servizio: {serviceName}</p>
            )}
          </div>

          {/* Filter selector */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">A chi vuoi inviare?</Label>
            <RadioGroup value={filter} onValueChange={setFilter} className="gap-2">
              {FILTERS.map((f) => {
                const FIcon = f.icon;
                return (
                  <label
                    key={f.value}
                    htmlFor={`filter-${f.value}`}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-muted/50 ${
                      filter === f.value ? 'border-primary bg-primary/5' : ''
                    }`}
                    data-testid={`filter-${f.value}`}
                  >
                    <RadioGroupItem value={f.value} id={`filter-${f.value}`} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <FIcon className="w-4 h-4 text-muted-foreground" />
                        <p className="font-medium text-sm">{f.label}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{f.hint}</p>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          {/* Preview count */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Destinatari stimati</p>
              <p className="font-semibold text-lg" data-testid="preview-count">
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `${previewCount ?? 0} cliente${(previewCount ?? 0) === 1 ? '' : 'i'}`}
              </p>
            </div>
            <Users className="w-8 h-8 text-primary/60" />
          </div>

          {/* Preview message */}
          <div className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 p-3 text-sm">
            <p className="text-xs text-green-700 dark:text-green-400 font-semibold mb-1">📲 Anteprima messaggio</p>
            <p className="text-xs whitespace-pre-line text-foreground/80">
              {`Ciao Maria! 👋

Si è appena liberato un appuntamento:

🗓 ${dateLabel} alle ore ${timeLabel}

Se ti interessa, rispondi a questo messaggio o chiamaci subito!`}
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="broadcast-cancel-btn">
            Annulla
          </Button>
          <Button
            onClick={sendBroadcast}
            disabled={sending || !previewCount || previewCount === 0}
            data-testid="broadcast-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio in corso...</>
            ) : (
              <><Megaphone className="w-4 h-4 mr-2" />Invia a {previewCount ?? 0} client{(previewCount ?? 0) === 1 ? 'e' : 'i'}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SlotBroadcastDialog;
